import {
    IonCard,
    IonCardContent,
    IonCol,
    IonIcon,
    IonLabel,
    IonRow,
    IonSelectOption,
    IonText,
    IonToggle,
    useIonToast,
} from '@ionic/react';
import { Trans, t } from '@lingui/macro';
import classNames from 'classnames';
import { Form, Formik, FormikHelpers } from 'formik';
import { home } from 'ionicons/icons';
import { useAtom } from 'jotai';
import React, { memo, useCallback, useMemo, useState } from 'react';
import * as Yup from 'yup';

import LoadableContent from '../../components/content/LoadableContent';
import DevicesLocation from '../../components/deviceslocation/DevicesLocation';
import DefaultSelect from '../../components/forms/defaultselect/DefaultSelect';
import LoadingButton from '../../components/loadingbutton/LoadingButton';
import InterventionPage from '../../components/page/InterventionPage';
import PhotoUpload from '../../components/photoUpload/PhotoUpload';
import { IInstallationRouteProps } from '../../interfaces/IRoute';
import {
    accessTokenAtom,
    canPerformInstallationAtom,
    jobDetailsWithStateAtom,
    jobIdAtom,
    jobsListResetAtom,
    roomsWithDevicesAndStateAtom,
    startInstallationAtom,
    updateDBStateAtom,
} from '../../store/Atoms';
import {
    clearDevicesRequest,
    closeInterventionRequest,
    errorReasonRequest,
} from '../../store/Requests';
import { ImageCategories } from '../../types/ImageInfo';
import { addGroupsToReasons } from '../../utils/addGroupsToReasons';
import { MAX_IMAGE_NR } from '../../utils/constants';
import { getErrorMessage } from '../../utils/errorMessage';
import { useCustomRouter } from '../../utils/hooks/useCustomRouter';
import usePhotosUploaded from '../../utils/hooks/usePhotosUploaded';
import classes from '../installation/inspect/DoneError.module.css';

type UninstallErrorFormValues = {
    reason: {
        errorCode: string;
        errorLabel: string;
    };
    retainDevicesAtSite: boolean;
};

const UninstallErrorSchema = () =>
    Yup.object().shape({
        reason: Yup.object().shape({
            errorCode: Yup.string().required(t`Reason is required`),
            errorLabel: Yup.string().required(t`Reason is required`),
        }),
    });

const Inspect: React.FC<IInstallationRouteProps> = memo(() => {
    const router = useCustomRouter();
    const [presentToast] = useIonToast();
    const photosUploaded = usePhotosUploaded();
    const [value] = useAtom(jobDetailsWithStateAtom);
    const [accessToken] = useAtom(accessTokenAtom);
    const [jobId] = useAtom(jobIdAtom);
    const [, resetJobsList] = useAtom(jobsListResetAtom);
    const uninstallErrorReasons = addGroupsToReasons(
        value.state === 'hasData'
            ? value.data.errorReasons.filter(
                  (er) => er.installationType === 'uninstall'
              )
            : []
    );
    const [, startInstallation] = useAtom(startInstallationAtom);
    const [canPerformIntervention, setCanPerformIntervention] = useAtom(
        canPerformInstallationAtom
    );
    const [retainDevicesAtSite, setRetainDevicesAtSite] = useState(false);
    const [roomsValue] = useAtom(roomsWithDevicesAndStateAtom);
    const devicesList = useMemo(
        () =>
            roomsValue.state !== 'hasData'
                ? []
                : roomsValue.data.map((r) => r.devices).flat(),
        [roomsValue]
    );
    const [, updateDBState] = useAtom(updateDBStateAtom);
    const [loading, setLoading] = useState(false);
    const enableProceed = canPerformIntervention && photosUploaded;

    const handleFormSubmit = useCallback(
        async (
            data: UninstallErrorFormValues,
            actions: FormikHelpers<UninstallErrorFormValues>
        ) => {
            actions.setSubmitting(true);
            try {
                await startInstallation();
                await errorReasonRequest(
                    accessToken,
                    jobId,
                    data.reason.errorCode,
                    retainDevicesAtSite
                );
                await closeInterventionRequest(true);
                resetJobsList();
                router.push('/?interventionCompleted=true', 'back');
            } catch (e: any) {
                await presentToast({
                    message: getErrorMessage(
                        e,
                        t`Oops, something went wrong while closing uninstallation`
                    ),
                    duration: 5000,
                    color: 'danger',
                    buttons: [
                        {
                            text: t`Dismiss`,
                            role: 'cancel',
                        },
                    ],
                });
            }
            actions.setSubmitting(false);
        },
        [
            accessToken,
            jobId,
            presentToast,
            startInstallation,
            resetJobsList,
            retainDevicesAtSite,
            router,
        ]
    );

    const onProceedHandler = useCallback(async () => {
        setLoading(true);
        try {
            await startInstallation();
            await clearDevicesRequest();
            await updateDBState();
            router.push('remove', 'forward', 'push');
        } catch (e: any) {
            const errorMessage =
                e.reason || e.message || t`Oops, something went wrong!`;
            presentToast({
                message: errorMessage,
                duration: 5000,
                color: 'danger',
                buttons: [
                    {
                        text: t`Dismiss`,
                        role: 'cancel',
                    },
                ],
            });
        }
        setLoading(false);
    }, [startInstallation, updateDBState, router, presentToast]);

    return (
        <InterventionPage title={t`Inspect`} withPadding>
            <LoadableContent value={value}>
                <Formik
                    initialValues={{
                        reason: {
                            errorCode: '',
                            errorLabel: '',
                        },
                        retainDevicesAtSite: true,
                    }}
                    validationSchema={UninstallErrorSchema()}
                    validateOnMount={true}
                    onSubmit={handleFormSubmit}
                >
                    {({
                        values,
                        handleChange,
                        handleBlur,
                        errors,
                        touched,
                        isSubmitting,
                        isValid,
                    }) => (
                        <Form>
                            <IonCard className="ion-margin-bottom">
                                <IonCardContent>
                                    <IonText>
                                        <IonIcon
                                            icon={home}
                                            size="large"
                                            className="ion-float-left ion-padding-end"
                                        />
                                        <Trans>
                                            Uninstallation inspection
                                        </Trans>
                                        <br />
                                        <Trans>
                                            Please provide basic information on
                                            the home and verify the feasibility
                                            of the uninstallation.
                                        </Trans>
                                    </IonText>
                                </IonCardContent>
                            </IonCard>
                            <PhotoUpload
                                imageCode={
                                    ImageCategories.photosBeforeInstallation
                                }
                                maxNumber={MAX_IMAGE_NR}
                                label={t`Photo before uninstall`}
                            ></PhotoUpload>
                            <PhotoUpload
                                imageCode={
                                    ImageCategories.safetyRisksOrAnomalies
                                }
                                maxNumber={MAX_IMAGE_NR}
                                label={t`Photo of risks or anomalies`}
                            ></PhotoUpload>
                            <IonRow className="ion-justify-content-between ion-align-items-center ion-margin-top ion-margin-bottom">
                                <IonCol>
                                    <IonLabel>
                                        <Trans>
                                            Can you perform the uninstallation?
                                        </Trans>
                                    </IonLabel>
                                </IonCol>
                                <IonCol size="auto">
                                    <IonToggle
                                        color="primary"
                                        onIonChange={(e) =>
                                            setCanPerformIntervention(
                                                e.detail.checked
                                            )
                                        }
                                        checked={canPerformIntervention}
                                    />
                                </IonCol>
                            </IonRow>
                            {!canPerformIntervention ? (
                                <>
                                    <IonRow className="ion-justify-content-between ion-align-items-center ion-margin-top ion-margin-bottom">
                                        <IonCol>
                                            <DefaultSelect
                                                className={classNames({
                                                    'ion-invalid':
                                                        errors.reason,
                                                    'ion-touched':
                                                        touched.reason,
                                                })}
                                                name="reason"
                                                value={values.reason}
                                                label={t`Reason`}
                                                placeholder={t`Select a reason`}
                                                onIonChange={handleChange}
                                                onIonBlur={handleBlur}
                                                errorText={
                                                    errors.reason?.errorCode
                                                }
                                            >
                                                {uninstallErrorReasons.map(
                                                    (reason) =>
                                                        // error groups have empty errorCode
                                                        reason.errorCode ? (
                                                            <IonSelectOption
                                                                key={
                                                                    reason.errorCode
                                                                }
                                                                value={reason}
                                                                className={
                                                                    classes.errorReasonOption
                                                                }
                                                            >
                                                                {
                                                                    reason.errorLabel
                                                                }
                                                            </IonSelectOption>
                                                        ) : (
                                                            <IonSelectOption
                                                                key={`group:${reason.errorGroup}`}
                                                                value={reason}
                                                                disabled
                                                                className={
                                                                    classes.errorReasonGroup
                                                                }
                                                            >
                                                                {
                                                                    reason.errorGroup
                                                                }
                                                            </IonSelectOption>
                                                        )
                                                )}
                                            </DefaultSelect>
                                        </IonCol>
                                    </IonRow>
                                    {values.reason.errorCode ? (
                                        <DevicesLocation
                                            devicesList={devicesList}
                                            retainDevicesAtSite={
                                                retainDevicesAtSite
                                            }
                                            onToggleChange={(e) => {
                                                setRetainDevicesAtSite(
                                                    e.detail.checked
                                                );
                                                values.retainDevicesAtSite =
                                                    e.detail.checked;
                                            }}
                                        />
                                    ) : (
                                        <></>
                                    )}
                                    <LoadingButton
                                        className="ion-margin-top"
                                        disabled={
                                            !values.reason.errorCode ||
                                            (!retainDevicesAtSite &&
                                                devicesList.length > 0)
                                        }
                                        loading={isSubmitting}
                                        type="submit"
                                    >
                                        <Trans>Close uninstallation</Trans>
                                    </LoadingButton>
                                </>
                            ) : (
                                <LoadingButton
                                    disabled={!enableProceed}
                                    loading={loading}
                                    onClick={onProceedHandler}
                                >
                                    <Trans>Proceed with uninstallation</Trans>
                                </LoadingButton>
                            )}
                        </Form>
                    )}
                </Formik>
            </LoadableContent>
        </InterventionPage>
    );
});

export default Inspect;
